import {
	BottomSheetFlatList,
	type BottomSheetFlatListMethods,
} from "@gorhom/bottom-sheet";
import { setOpacity } from "@memorang/helpers/setOpacity";
import { LinearGradient } from "expo-linear-gradient";
/* ----------------- Globals --------------- */
import { useEffect, useReducer, useRef } from "react";
import {
	FlatList,
	type LayoutChangeEvent,
	type NativeSyntheticEvent,
} from "react-native";

/* ----------------- Types --------------- */
import type { NativeScrollEvent } from "react-native";

/* -----------------  UI --------------- */
import { IconButton } from "react-native-paper";
import Box from "../Box";

/* -----------------  Helpers & Hooks --------------- */
import useBreakpoints from "../../useBreakpoints";

const SCROLL_EDGE_THRESHOLD = 2;
const OVERFLOW_THRESHOLD = 1; // Minimum difference to consider as overflow
const EDGE_FADE_WIDTH = 48;
const GRADIENT_START = { x: 0, y: 0.5 };
const GRADIENT_END = { x: 1, y: 0.5 };
const edgeFadeStyle = {
	position: "absolute",
	top: 0,
	bottom: 0,
	width: EDGE_FADE_WIDTH,
} as const;

const computeIsAtEnd = (
	cw: number | null,
	lw: number | null,
	offset: number,
) => {
	if (cw == null || lw == null) {
		return false;
	}
	const maxScroll = cw - lw;
	return maxScroll <= 0 || offset >= maxScroll - SCROLL_EDGE_THRESHOLD;
};

type Props<T> = React.ComponentProps<typeof FlatList<T>> & {
	useBottomSheetFlatList?: boolean;
	buttonContainerColor?: string;
	buttonIconColor?: string;
	/** Wrap into a multi-row grid at `smUp` instead of scrolling. Defaults to `true`. */
	wrapOnLargeScreens?: boolean;
	/** Distance the arrow buttons scroll per click. Defaults to 300. */
	scrollStep?: number;
	/**
	 * Fades content into this color at whichever edge still has items to scroll
	 * to. Pass the color of the surface behind the list; omit to skip the fade.
	 */
	edgeFadeColor?: string;
};

type State = {
	scrollPosition: number;
	isAtStart: boolean;
	isAtEnd: boolean;
	hasOverflow: boolean | null;
	layoutWidth: number | null;
	contentWidth: number | null;
	isHovered: boolean;
};

type Action =
	| { type: "SET_SCROLL_POSITION"; payload: number }
	| { type: "SET_IS_AT_START"; payload: boolean }
	| { type: "SET_IS_AT_END"; payload: boolean }
	| { type: "SET_HAS_OVERFLOW"; payload: boolean | null }
	| { type: "SET_LAYOUT_WIDTH"; payload: number | null }
	| { type: "SET_CONTENT_WIDTH"; payload: number | null }
	| { type: "SET_IS_HOVERED"; payload: boolean };

const initialState: State = {
	scrollPosition: 0,
	isAtStart: true,
	isAtEnd: false,
	hasOverflow: null,
	layoutWidth: null,
	contentWidth: null,
	isHovered: false,
};

function reducer(state: State, action: Action): State {
	switch (action.type) {
		case "SET_SCROLL_POSITION":
			return { ...state, scrollPosition: action.payload };
		case "SET_IS_AT_START":
			return { ...state, isAtStart: action.payload };
		case "SET_IS_AT_END":
			return { ...state, isAtEnd: action.payload };
		case "SET_HAS_OVERFLOW":
			return { ...state, hasOverflow: action.payload };
		case "SET_LAYOUT_WIDTH":
			return { ...state, layoutWidth: action.payload };
		case "SET_CONTENT_WIDTH":
			return { ...state, contentWidth: action.payload };
		case "SET_IS_HOVERED":
			return { ...state, isHovered: action.payload };
		default:
			return state;
	}
}

export function ScrollButtonFlatlist<T>({
	buttonContainerColor,
	buttonIconColor,
	wrapOnLargeScreens = true,
	scrollStep = 300,
	edgeFadeColor,
	...props
}: Props<T>) {
	const scrollRef = useRef<BottomSheetFlatListMethods>(null);
	const scrollPositionRef = useRef(0);
	const { smUp } = useBreakpoints();
	const [
		{
			scrollPosition,
			isAtStart,
			isAtEnd,
			hasOverflow,
			layoutWidth,
			contentWidth,
			isHovered,
		},
		dispatch,
	] = useReducer(reducer, initialState);

	const shouldWrap = smUp && wrapOnLargeScreens;

	const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
		const offsetX = event.nativeEvent.contentOffset.x;
		scrollPositionRef.current = offsetX;
		dispatch({ type: "SET_SCROLL_POSITION", payload: offsetX });
		dispatch({
			type: "SET_IS_AT_START",
			payload: offsetX <= SCROLL_EDGE_THRESHOLD,
		});
		if (contentWidth != null && layoutWidth != null) {
			dispatch({
				type: "SET_IS_AT_END",
				payload: computeIsAtEnd(contentWidth, layoutWidth, offsetX),
			});
			dispatch({
				type: "SET_HAS_OVERFLOW",
				payload: contentWidth > layoutWidth + OVERFLOW_THRESHOLD,
			});
		}
	};

	const scrollBy = (offset: number) => {
		let newScrollPosition = scrollPosition + offset;
		newScrollPosition = Math.max(0, newScrollPosition);
		scrollRef?.current?.scrollToOffset({
			offset: newScrollPosition,
			animated: true,
		});
		dispatch({ type: "SET_SCROLL_POSITION", payload: newScrollPosition });
	};

	const handleOnLayout = (event: LayoutChangeEvent) => {
		const newLayoutWidth = event.nativeEvent.layout.width;
		dispatch({ type: "SET_LAYOUT_WIDTH", payload: newLayoutWidth });
		if (contentWidth !== null) {
			dispatch({
				type: "SET_IS_AT_END",
				payload: computeIsAtEnd(
					contentWidth,
					newLayoutWidth,
					scrollPositionRef.current,
				),
			});
			dispatch({
				type: "SET_HAS_OVERFLOW",
				payload: contentWidth > newLayoutWidth + OVERFLOW_THRESHOLD,
			});
		}
	};

	const handleContentSizeChange = (newContentWidth: number) => {
		dispatch({ type: "SET_CONTENT_WIDTH", payload: newContentWidth });
		if (layoutWidth !== null) {
			dispatch({
				type: "SET_IS_AT_END",
				payload: computeIsAtEnd(
					newContentWidth,
					layoutWidth,
					scrollPositionRef.current,
				),
			});
			dispatch({
				type: "SET_HAS_OVERFLOW",
				payload: newContentWidth > layoutWidth + OVERFLOW_THRESHOLD,
			});
		}
	};

	// Dynamically check for overflow when dimensions change
	useEffect(() => {
		if (contentWidth !== null && layoutWidth !== null) {
			dispatch({
				type: "SET_IS_AT_END",
				payload: computeIsAtEnd(
					contentWidth,
					layoutWidth,
					scrollPositionRef.current,
				),
			});
			dispatch({
				type: "SET_HAS_OVERFLOW",
				payload: contentWidth > layoutWidth + OVERFLOW_THRESHOLD,
			});
		}
	}, [contentWidth, layoutWidth]);

	// Whether to show scroll buttons
	// Only show buttons when there's actual overflow (contentWidth > layoutWidth)
	const showLeftScrollButton =
		hasOverflow === true && !isAtStart && smUp && isHovered;
	const showRightScrollButton =
		hasOverflow === true && !isAtEnd && smUp && isHovered;

	const List = props.useBottomSheetFlatList ? BottomSheetFlatList : FlatList;

	// Extract gap from contentContainerStyle (can be array or object)
	const getGap = (): number => {
		if (!props.contentContainerStyle) {
			return 12;
		}
		const styleArray = Array.isArray(props.contentContainerStyle)
			? props.contentContainerStyle
			: [props.contentContainerStyle];

		for (const style of styleArray) {
			if (style && typeof style === "object" && "gap" in style) {
				const gapValue = (style as { gap?: number }).gap;
				if (typeof gapValue === "number") {
					return gapValue;
				}
			}
		}
		return 12;
	};

	const getContainerStyle = () => {
		if (!props.contentContainerStyle) {
			return undefined;
		}
		if (Array.isArray(props.contentContainerStyle)) {
			return props.contentContainerStyle.map((s) => {
				if (s && typeof s === "object" && "gap" in s) {
					const { gap: _, ...rest } = s as { gap?: number };
					return rest;
				}
				return s;
			});
		}
		if (
			typeof props.contentContainerStyle === "object" &&
			"gap" in props.contentContainerStyle
		) {
			const { gap: _, ...rest } = props.contentContainerStyle as {
				gap?: number;
			};
			return rest;
		}
		return props.contentContainerStyle;
	};

	if (
		shouldWrap &&
		props.data &&
		Array.isArray(props.data) &&
		props.renderItem
	) {
		const gap = getGap();
		const containerStyle = getContainerStyle();
		const renderItem = props.renderItem;

		return (
			<Box
				flexDirection="row"
				flexWrap="wrap"
				gap={gap}
				style={containerStyle}
				onLayout={handleOnLayout}
			>
				{props.data.map((item, index) => {
					const key = props.keyExtractor
						? props.keyExtractor(item, index)
						: `item-${index}`;
					return (
						<Box key={key}>
							{renderItem({
								item,
								index,
							} as Parameters<typeof renderItem>[0])}
						</Box>
					);
				})}
			</Box>
		);
	}

	return (
		<Box
			flexDirection="row"
			alignItems="center"
			onMouseEnter={() => {
				dispatch({ type: "SET_IS_HOVERED", payload: true });
			}}
			onMouseLeave={() => {
				dispatch({ type: "SET_IS_HOVERED", payload: false });
			}}
		>
			<List
				{...props}
				horizontal
				ref={scrollRef}
				onScroll={handleScroll}
				onEndReachedThreshold={0.001}
				showsHorizontalScrollIndicator={false}
				snapToInterval={props.snapToInterval ?? 200}
				decelerationRate="fast"
				scrollEventThrottle={16}
				onContentSizeChange={handleContentSizeChange}
				onLayout={handleOnLayout}
			/>
			{edgeFadeColor && hasOverflow === true ? (
				<EdgeFades color={edgeFadeColor} atStart={isAtStart} atEnd={isAtEnd} />
			) : null}
			{showLeftScrollButton && (
				<Box
					position="absolute"
					left={0}
					top={0}
					bottom={0}
					justifyContent="center"
				>
					<ScrollIconButton
						direction="left"
						scrollBy={scrollBy}
						scrollStep={scrollStep}
						containerColor={buttonContainerColor}
						iconColor={buttonIconColor}
					/>
				</Box>
			)}
			{showRightScrollButton && (
				<Box
					position="absolute"
					right={0}
					top={0}
					bottom={0}
					justifyContent="center"
				>
					<ScrollIconButton
						direction="right"
						scrollBy={scrollBy}
						scrollStep={scrollStep}
						containerColor={buttonContainerColor}
						iconColor={buttonIconColor}
					/>
				</Box>
			)}
		</Box>
	);
}

type EdgeFadesProps = {
	color: string;
	atStart: boolean;
	atEnd: boolean;
};

/** Hints that the row keeps going by fading items into the surface behind it. */
const EdgeFades = ({ color, atStart, atEnd }: EdgeFadesProps) => {
	const transparent = setOpacity(color, 0);
	return (
		<>
			{atStart ? null : (
				<LinearGradient
					colors={[color, transparent]}
					start={GRADIENT_START}
					end={GRADIENT_END}
					pointerEvents="none"
					style={{ ...edgeFadeStyle, left: 0 }}
				/>
			)}
			{atEnd ? null : (
				<LinearGradient
					colors={[transparent, color]}
					start={GRADIENT_START}
					end={GRADIENT_END}
					pointerEvents="none"
					style={{ ...edgeFadeStyle, right: 0 }}
				/>
			)}
		</>
	);
};

type ScrollIconButtonProps = {
	direction: "left" | "right";
	scrollBy: (offset: number) => void;
	scrollStep: number;
	containerColor?: string;
	iconColor?: string;
};

const ScrollIconButton = ({
	direction = "right",
	scrollBy,
	scrollStep,
	containerColor,
	iconColor,
}: ScrollIconButtonProps) => {
	return (
		<IconButton
			icon={`arrow-${direction}`}
			mode="contained-tonal"
			containerColor={containerColor}
			iconColor={iconColor}
			animated
			size={24}
			onPress={() => scrollBy(direction === "right" ? scrollStep : -scrollStep)}
		/>
	);
};
